import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import QuoteModal from "./components/QuoteModal";
import BookingModal from "./components/BookingModal";

// Pages
import Home from "./pages/Home";
import Services from "./pages/Services";
import CanopyDuctFanCleaning from "./pages/CanopyDuctFanCleaning";
import CommercialKitchenCleaning from "./pages/CommercialKitchenCleaning";
import WarehouseIndustrialCleaning from "./pages/WarehouseIndustrialCleaning";
import CommercialOfficeCleaning from "./pages/CommercialOfficeCleaning";
import CommercialWindowCleaning from "./pages/CommercialWindowCleaning";
import CommercialCarpetCleaning from "./pages/CommercialCarpetCleaning";
import CommercialHighPressureCleaning from "./pages/CommercialHighPressureCleaning";
import CommercialPizzaOvenCleaning from "./pages/CommercialPizzaOvenCleaning";
import SchoolsEducationalFacilities from "./pages/SchoolsEducationalFacilities";
import StrataApartmentCleaning from "./pages/StrataApartmentCleaning";
import GovernmentBuildingCleaning from "./pages/GovernmentBuildingCleaning";
import AgedCareHealthcareCleaning from "./pages/AgedCareHealthcareCleaning";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Clients from "./pages/Clients";
import FAQ from "./pages/FAQ";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import NotFound from "./pages/NotFound";

// Admin Pages
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard, { 
  DashboardHome, 
  ServicesManagement, 
  TeamManagement, 
  ClientsManagement, 
  SettingsManagement,
  TestimonialsManagement,
  FAQManagement 
} from "./pages/AdminDashboard";
import { AuthProvider } from "./context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Scroll to top component and Analytics page tracking
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Track page view with Google Analytics (GA4)
    if (window.gtag) {
      window.gtag('config', 'G-F5KBV3Y0BP', {
        page_path: pathname,
      });
    }
  }, [pathname]);
  
  return null;
}

function App() {
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App min-h-screen flex flex-col">
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
            {/* Admin Routes - No Navbar/Footer */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />}>
              <Route path="dashboard" element={<DashboardHome />} />
              <Route path="services" element={<ServicesManagement />} />
              <Route path="team" element={<TeamManagement />} />
              <Route path="clients" element={<ClientsManagement />} />
              <Route path="testimonials" element={<TestimonialsManagement />} />
              <Route path="faqs" element={<FAQManagement />} />
              <Route path="settings" element={<SettingsManagement />} />
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
            </Route>
            
            {/* Public Routes - With Navbar/Footer */}
            <Route path="/*" element={
              <>
                <Navbar 
                  onQuoteClick={() => setQuoteModalOpen(true)}
                  onBookingClick={() => setBookingModalOpen(true)}
                />
                <main className="flex-grow">
                  <Routes>
                    <Route 
                      path="/" 
                      element={
                        <Home 
                          onQuoteClick={() => setQuoteModalOpen(true)}
                          onBookingClick={() => setBookingModalOpen(true)}
                        />
                      } 
                    />
                    <Route path="/services" element={<Services />} />
                    <Route 
                      path="/services/canopy-duct-fan-cleaning-perth" 
                      element={<CanopyDuctFanCleaning onQuoteClick={() => setQuoteModalOpen(true)} />} 
                    />
                    <Route 
                      path="/services/commercial-kitchen-cleaning-perth" 
                      element={<CommercialKitchenCleaning onQuoteClick={() => setQuoteModalOpen(true)} />} 
                    />
                    <Route 
                      path="/services/warehouse-industrial-cleaning-perth" 
                      element={<WarehouseIndustrialCleaning onQuoteClick={() => setQuoteModalOpen(true)} />} 
                    />
                    <Route 
                      path="/services/commercial-office-cleaning-perth" 
                      element={<CommercialOfficeCleaning onQuoteClick={() => setQuoteModalOpen(true)} />} 
                    />
                    <Route 
                      path="/services/commercial-window-cleaning-perth" 
                      element={<CommercialWindowCleaning onQuoteClick={() => setQuoteModalOpen(true)} />} 
                    />
                    <Route 
                      path="/services/commercial-carpet-cleaning-perth" 
                      element={<CommercialCarpetCleaning onQuoteClick={() => setQuoteModalOpen(true)} />} 
                    />
                    <Route 
                      path="/services/commercial-high-pressure-cleaning-perth" 
                      element={<CommercialHighPressureCleaning onQuoteClick={() => setQuoteModalOpen(true)} />} 
                    />
                    <Route 
                      path="/services/commercial-pizza-oven-cleaning-perth" 
                      element={<CommercialPizzaOvenCleaning onQuoteClick={() => setQuoteModalOpen(true)} />} 
                    />
                    <Route 
                      path="/industries/schools-educational-facilities-perth" 
                      element={<SchoolsEducationalFacilities onQuoteClick={() => setQuoteModalOpen(true)} />} 
                    />
                    <Route 
                      path="/industries/strata-apartment-cleaning-perth" 
                      element={<StrataApartmentCleaning onQuoteClick={() => setQuoteModalOpen(true)} />} 
                    />
                    <Route 
                      path="/industries/government-building-cleaning-perth" 
                      element={<GovernmentBuildingCleaning onQuoteClick={() => setQuoteModalOpen(true)} />} 
                    />
                    <Route 
                      path="/industries/aged-care-healthcare-cleaning-perth" 
                      element={<AgedCareHealthcareCleaning onQuoteClick={() => setQuoteModalOpen(true)} />} 
                    />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/clients" element={<Clients />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/terms-of-service" element={<TermsOfService />} />
                    
                    {/* Legacy URL Redirects - with and without trailing slashes */}
                    <Route path="/services/canopy-cleaning" element={<Navigate to="/services/canopy-duct-fan-cleaning-perth" replace />} />
                    <Route path="/services/canopy-cleaning/" element={<Navigate to="/services/canopy-duct-fan-cleaning-perth" replace />} />
                    <Route path="/services/warehouse-factory-cleaning" element={<Navigate to="/services/warehouse-industrial-cleaning-perth" replace />} />
                    <Route path="/services/warehouse-factory-cleaning/" element={<Navigate to="/services/warehouse-industrial-cleaning-perth" replace />} />
                    <Route path="/services/stainless-steel-benches-racks-and-shelves-cleaning" element={<Navigate to="/services/commercial-kitchen-cleaning-perth" replace />} />
                    <Route path="/services/stainless-steel-benches-racks-and-shelves-cleaning/" element={<Navigate to="/services/commercial-kitchen-cleaning-perth" replace />} />
                    <Route path="/services/commercial-kitchen-cleaning" element={<Navigate to="/services/commercial-kitchen-cleaning-perth" replace />} />
                    <Route path="/services/commercial-kitchen-cleaning/" element={<Navigate to="/services/commercial-kitchen-cleaning-perth" replace />} />
                    <Route path="/services/pressure-roof-cleaning" element={<Navigate to="/services/commercial-high-pressure-cleaning-perth" replace />} />
                    <Route path="/services/pressure-roof-cleaning/" element={<Navigate to="/services/commercial-high-pressure-cleaning-perth" replace />} />
                    <Route path="/services/paver-cleaning" element={<Navigate to="/services/commercial-high-pressure-cleaning-perth" replace />} />
                    <Route path="/services/paver-cleaning/" element={<Navigate to="/services/commercial-high-pressure-cleaning-perth" replace />} />
                    <Route path="/services/microwave-toaster-cleaning" element={<Navigate to="/services/commercial-kitchen-cleaning-perth" replace />} />
                    <Route path="/services/microwave-toaster-cleaning/" element={<Navigate to="/services/commercial-kitchen-cleaning-perth" replace />} />
                    <Route path="/product/rack-off" element={<Navigate to="/services/canopy-duct-fan-cleaning-perth" replace />} />
                    <Route path="/product/rack-off/" element={<Navigate to="/services/canopy-duct-fan-cleaning-perth" replace />} />
                    
                    {/* 404 Page - Catch all unknown routes */}
                    <Route path="*" element={<NotFound onQuoteClick={() => setQuoteModalOpen(true)} />} />
                  </Routes>
                </main>
                <Footer />
                <QuoteModal 
                  isOpen={quoteModalOpen} 
                  onClose={() => setQuoteModalOpen(false)} 
                />
                <BookingModal 
                  isOpen={bookingModalOpen} 
                  onClose={() => setBookingModalOpen(false)} 
                />
              </>
            } />
          </Routes>
        </BrowserRouter>
        </div>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
